import { z } from 'zod';
import {
  collection,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
} from 'firebase/firestore';
import { ALL_EMAIL_EVENT_TYPES, EmailEventType } from '@etiquetachef/types';
import { firestore } from './firebase';

/**
 * Leitura dos logs de e-mail transacional (`/emailLogs`) para a aba Logs de
 * `/billing/emails` do admin.
 *
 * Origem: #795 — 2026-10-03 14:12 +0200 (ADM-5 da auditoria geral). O
 * listener em si nasceu em `ae81f4d21` (2026-05-05, Sprint 16) dentro de
 * `firestoreTenantDetail.ts`, que continua dono do `subscribeEmailLogsByTenant`.
 *
 * Por quê: o admin lia um formato de doc que NUNCA existiu — `custom_args.*`
 * (templateId/locale/tenantId) e um array `events[]` no próprio doc. O
 * escritor real grava esses campos no TOPO e os eventos numa SUBCOLEÇÃO. O
 * resultado era template/idioma/empresa "—" em toda linha, "Último evento"
 * sempre "—", e o filtro por empresa (`where('custom_args.tenantId')` +
 * `orderBy('createdAt')`, sem índice composto) caindo em `failed-precondition`.
 *
 * Contrato:
 *  - `toEmailLogEntry(id, raw)` → `EmailLogEntry` ou `null` (fora do schema —
 *    o chamador reporta).
 *  - `pickLastEvent(raws)` → o evento de maior `timestamp` (instante do
 *    EVENTO no SendGrid, não da chegada); empate no mesmo segundo: `processed`
 *    perde, porque é sempre o primeiro estágio.
 *  - `fetchEmailLastEvent(logId)` → 1 leitura de até `LAST_EVENT_PROBE` docs
 *    de `/emailLogs/{logId}/events` (índice automático de campo único em
 *    `timestamp`, escopo COLLECTION). Lança o erro do Firestore.
 *
 * Usado por: `firestoreTenantDetail.ts` (`subscribeEmailLogsByTenant`, que
 * mapeia cada doc) e `features/billing/EmailLogsTable.tsx` (célula "Último
 * evento") — web `apps/admin`, super-admin (`/billing/emails`).
 *
 * Arquitetura: escrita só pelas Functions (rules: leitura super-admin,
 * escrita `false`). O último evento vem da SUBCOLEÇÃO, e não do `lastEvent`
 * do doc pai, de propósito: o webhook processa o lote em paralelo
 * (`Promise.all`) e faz `merge` sem guarda de ordem, então `lastEvent` é o
 * último a CHEGAR, não o mais recente — `processed` e `delivered` no mesmo
 * lote podem deixar `processed`. O comentário de `persistEvent` em
 * `sendgridEventsWebhook.ts` registra isso e diz que, se alguém passar a ler
 * `lastEvent`, a guarda por `timestamp` vira obrigatória lá.
 *
 * Dados — `/emailLogs/{idempotencyKey}`, escrito por
 * `apps/functions/src/lib/billing/sendInternalEmail.ts` (create) e mesclado
 * por `apps/functions/src/email/sendgridEventsWebhook.ts` (a cada evento).
 * Exemplo anonimizado:
 *   {
 *     "idempotencyKey": "invoice-new:tenant-1:inv_0001",
 *     "templateId": "INVOICE_NEW",
 *     "locale": "pt-BR",
 *     "to": "cliente@example.com",
 *     "tenantId": "tenant-1",              // null em e-mail sem empresa
 *     "callerUid": "__system__",
 *     "messageId": "msg-000",              // '' até o SendGrid aceitar
 *     "status": "SENT",                    // SENDING | SENT | BLOCKED | …
 *     "createdAt": Timestamp,              // serverTimestamp
 *     "updatedAt": Timestamp,
 *     "lastEvent": "delivered",            // ver Arquitetura: NÃO confiável
 *     "lastEventAt": Timestamp,            // chegada do último evento
 *     "lastEventReason": null
 *   }
 * `/emailLogs/{key}/events/{sg_event_id}` — o evento do SendGrid espalhado
 * (`SendGridEventSchema`, `packages/types/src/email.ts`):
 *   { "event": "delivered", "timestamp": 1790000000, "email": "…",
 *     "sg_event_id": "…", "reason": "…" (opcional), "receivedAt": Timestamp }
 * `timestamp` é unix em SEGUNDOS.
 *
 * Falhas conhecidas:
 *  - 2026-05-05 → 2026-10-03 (#795, ADM-5): leitura de `custom_args`/`events[]`
 *    inexistentes + filtro sem índice (ver Por quê).
 *
 * Dicas: o filtro por empresa é `where('tenantId') + orderBy('createdAt',
 * 'desc')` e exige o índice composto `emailLogs (tenantId ASC, createdAt
 * DESC)` em `firebase/firestore.indexes.json`.
 *
 * Issues: #795 (ADM-5).
 *
 * Histórico:
 *  - 2026-10-03 14:12 +0200 — (#795, ADM-5) criado.
 */

/** Uma linha da tabela de logs. */
export type EmailLogEntry = {
  id: string;
  templateId: string | null;
  to: string | null;
  locale: string | null;
  tenantId: string | null;
  messageId: string | null;
  createdAt: number | null;
  status: string | null;
  /**
   * `lastEventAt` do doc pai, em ms: quando o ÚLTIMO evento CHEGOU. Não é o
   * instante do evento — serve só para a célula saber que há evento e reler a
   * subcoleção quando chegar outro. `null` = nenhum evento ainda.
   */
  lastEventReceivedAt: number | null;
};

/** O evento mais recente de um e-mail, lido da subcoleção `events`. */
export type EmailLastEvent = {
  event: EmailEventType;
  /** Instante do evento no SendGrid, em ms. */
  at: number;
  reason: string | null;
};

/** Até quantos eventos do topo ler para desempatar o mesmo segundo. */
const LAST_EVENT_PROBE = 5;

/** `Timestamp` do SDK (objeto com `toMillis`) ou número em ms. */
const MillisSchema = z
  .union([
    z.number(),
    z.custom<{ toMillis: () => number }>(
      (v) =>
        typeof v === 'object' &&
        v !== null &&
        typeof (v as { toMillis?: unknown }).toMillis === 'function',
    ),
  ])
  .transform((v) => (typeof v === 'number' ? v : v.toMillis()));

/**
 * O doc de `/emailLogs` como `sendInternalEmail` + o webhook gravam. Todo
 * campo é opcional/anulável: o sentinela nasce antes do envio (`messageId: ''`,
 * `status: 'SENDING'`) e o webhook só acrescenta. Tipo errado num campo é que
 * descarta a linha.
 */
const EmailLogDocSchema = z.object({
  templateId: z.string().nullish(),
  locale: z.string().nullish(),
  to: z.string().nullish(),
  tenantId: z.string().nullish(),
  messageId: z.string().nullish(),
  status: z.string().nullish(),
  createdAt: MillisSchema.nullish(),
  lastEventAt: MillisSchema.nullish(),
});

/**
 * Converte um doc de `/emailLogs` numa linha da tabela, ou `null` se ele não
 * passa no schema.
 *
 * Origem: #795 — commit 76bc108ad, 2026-10-03 (ADM-5).
 *
 * Usado por: `lib/firestoreTenantDetail.ts` (`subscribeEmailLogsByTenant` e
 * irmãos, que reportam o `null`).
 */
export function toEmailLogEntry(id: string, raw: unknown): EmailLogEntry | null {
  const parsed = EmailLogDocSchema.safeParse(raw);
  if (!parsed.success) return null;
  const d = parsed.data;
  // Bug corrigido em 2026-10-03 14:12 +0200 (#795, ADM-5): templateId,
  // locale e tenantId eram lidos de `custom_args.*`, que o escritor NUNCA
  // gravou no doc (`custom_args` só vai ao SendGrid, para ecoar no webhook) —
  // toda linha saía "—". Os campos moram no topo (`sendInternalEmail.ts`).
  return {
    id,
    templateId: d.templateId ?? null,
    to: d.to ?? null,
    locale: d.locale ?? null,
    tenantId: d.tenantId ?? null,
    // `''` é o sentinela "ainda não enviado", não um id.
    messageId: d.messageId ? d.messageId : null,
    createdAt: d.createdAt ?? null,
    status: d.status ?? null,
    lastEventReceivedAt: d.lastEventAt ?? null,
  };
}

/** Evento da subcoleção `events` — só os campos que o desempate usa. */
const EmailEventDocSchema = z.object({
  event: z.enum(ALL_EMAIL_EVENT_TYPES),
  timestamp: z.number().int().nonnegative(),
  reason: z.string().nullish(),
});

/**
 * Escolhe o evento mais recente pelo `timestamp` do SendGrid (segundos).
 * Empate: `processed` perde — é sempre o primeiro estágio, e `processed` +
 * `delivered` no mesmo segundo é o caso comum. Docs fora do schema são
 * ignorados (evento de tipo novo não derruba a célula).
 *
 * Origem: #795 — commit 76bc108ad, 2026-10-03 (ADM-5).
 *
 * Usado por: `fetchEmailLastEvent` (e testes deste módulo).
 */
export function pickLastEvent(raws: readonly unknown[]): EmailLastEvent | null {
  let best: z.infer<typeof EmailEventDocSchema> | null = null;
  for (const raw of raws) {
    const parsed = EmailEventDocSchema.safeParse(raw);
    if (!parsed.success) continue;
    const e = parsed.data;
    if (
      best === null ||
      e.timestamp > best.timestamp ||
      (e.timestamp === best.timestamp &&
        best.event === EmailEventType.PROCESSED &&
        e.event !== EmailEventType.PROCESSED)
    ) {
      best = e;
    }
  }
  return best === null ? null : { event: best.event, at: best.timestamp * 1000, reason: best.reason ?? null };
}

/**
 * Lê o evento mais recente de `/emailLogs/{logId}/events`. Ordena por
 * `timestamp` (instante do evento) e lê `LAST_EVENT_PROBE` docs para o
 * desempate de `pickLastEvent`.
 *
 * Origem: #795 — commit 76bc108ad, 2026-10-03 (ADM-5).
 *
 * Contrato: 1 leitura (`getDocs`) de até `LAST_EVENT_PROBE` docs; `null` sem
 * evento válido; lança o erro do Firestore (a célula trata).
 *
 * Usado por: `features/billing/EmailLogsTable.tsx` (célula "Último evento").
 *
 * Arquitetura: rule de `/emailLogs` (leitura super-admin); `orderBy` de campo
 * único usa o índice automático — não precisa de composto.
 */
export async function fetchEmailLastEvent(logId: string): Promise<EmailLastEvent | null> {
  const snap = await getDocs(
    query(
      collection(firestore, 'emailLogs', logId, 'events'),
      orderBy('timestamp', 'desc'),
      fsLimit(LAST_EVENT_PROBE),
    ),
  );
  return pickLastEvent(snap.docs.map((d) => d.data()));
}
